/** FLD-OFF-FIX-2 — the device storage budget.
 *
 *  A device was found holding 7.6 GB of cached views, which is what let iOS reclaim storage from
 *  under the PENDING PHOTO FILES. Rules here: the cache is CAPPED (setting, default 2 GB); when
 *  over the cap we evict drawing tiles first, then cached views (least recently cached, unpinned
 *  projects first); the outbox and its photo blobs are NEVER evicted by us, and persistent
 *  storage is requested so the OS leaves them alone too. */
import { idb } from "./db";

export const DEFAULT_CAP = 2 * 1024 * 1024 * 1024;
const DRAWINGS_CACHE = "drawings-offline-v1";

export const persistStorage = async () => {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch { return false; }
};

export const capBytes = async () => (await idb.get("meta", "cache_cap_bytes")) || DEFAULT_CAP;
export const setCapBytes = (n) => idb.set("meta", "cache_cap_bytes", n);

export async function usage() {
  try {
    const e = await navigator.storage.estimate();
    return { used: e.usage || 0, quota: e.quota || 0, persisted: !!(await navigator.storage.persisted?.()) };
  } catch { return { used: 0, quota: 0, persisted: false }; }
}

const size = (v) => { try { return new Blob([typeof v === "string" ? v : JSON.stringify(v)]).size; } catch { return 0; } };

/** Approximate bytes of cached views and pending photo files per project. */
export async function perProject() {
  const keys = await idb.keys("api-cache");
  const out = {};
  for (const k of keys) {
    const pid = (String(k).match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/) || [])[0] || "other";
    const hit = await idb.get("api-cache", k);
    const o = (out[pid] ||= { views: 0, view_bytes: 0, pending_photo_bytes: 0, pending_photos: 0 });
    o.views += 1; o.view_bytes += size(hit?.data);
  }
  for (const r of await idb.all("outbox")) {
    if (r.kind !== "photo" || !["pending", "sending", "failed"].includes(r.status)) continue;
    const o = (out[r.project_id || "other"] ||= { views: 0, view_bytes: 0, pending_photo_bytes: 0, pending_photos: 0 });
    o.pending_photos += 1;
    for (const p of r.body?.form || []) if (p.blob) o.pending_photo_bytes += (await idb.get("blobs", p.blob))?.size || 0;
  }
  return out;
}

/** Drop cached views (and the drawings cache) for a project. Pending items are untouched. */
export async function clearViews(projectId) {
  for (const k of await idb.keys("api-cache")) if (!projectId || String(k).includes(projectId)) await idb.del("api-cache", k);
  try {
    if (typeof caches !== "undefined") {
      const c = await caches.open(DRAWINGS_CACHE);
      for (const req of await c.keys()) if (!projectId || req.url.includes(projectId)) await c.delete(req);
    }
  } catch { /* no Cache API */ }
}

/** Bring the device under the cap. Order: drawing tiles → cached views of unpinned projects
 *  (oldest cached first) → pinned projects' views. Never blobs, never the outbox. */
export async function enforceCap() {
  const cap = await capBytes();
  let { used } = await usage();
  if (!used || used <= cap) return { evicted: 0, used, cap };
  let evicted = 0;
  try {
    if (typeof caches !== "undefined") {
      const c = await caches.open(DRAWINGS_CACHE);
      for (const req of await c.keys()) { await c.delete(req); evicted += 1; if ((await usage()).used <= cap) return { evicted, used: (await usage()).used, cap }; }
    }
  } catch { /* no Cache API */ }
  const meta = (await idb.get("meta", "projects")) || {};
  const byAge = Object.entries(meta).sort((a, b) => (a[1].pinned ? 1 : 0) - (b[1].pinned ? 1 : 0)
    || new Date(a[1].cached_at || 0) - new Date(b[1].cached_at || 0));
  for (const [pid] of byAge) {
    await clearViews(pid); evicted += 1;
    if ((await usage()).used <= cap) break;
  }
  used = (await usage()).used;
  return { evicted, used, cap };
}
